import React, { useEffect, useState } from 'react';
import { User, Mail, Phone, MapPin, CreditCard, Calendar, ShieldCheck, Briefcase, ArrowRightLeft, ArrowDownRight, ArrowUpRight, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Profile = ({ userData }: { userData?: any }) => {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState<any>(null);
  const [recentTxns, setRecentTxns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!userData || !userData.id) return;
      try {
        const profileRes = await fetch(`https://motilalalgo.com/api/profile/${userData.id}`);
        const profileJson = await profileRes.json();
        
        const txnRes = await fetch(`https://motilalalgo.com/api/profile/${userData.id}/transactions`);
        const txnJson = await txnRes.json();

        setProfileData(profileJson);
        setRecentTxns(txnJson);
      } catch (error) {
        console.error("Error fetching profile data", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [userData]);

  if (loading || !profileData) {
    return <div className="h-full flex items-center justify-center text-[#3b82f6]"><Loader className="animate-spin" size={40} /></div>;
  }

  const photoUrl = profileData?.photo_doc ? `https://motilalalgo.com/${profileData.photo_doc.replace(/\\/g, '/')}` : '';
  const joinDate = profileData?.created_at ? new Date(profileData.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A';

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-10 font-[Urbanist]">
      
      {/* Header */}
      <div className="px-2 md:px-0">
        <h1 className="text-3xl md:text-4xl font-black text-[#0f172a]">My Profile</h1>
        <p className="text-[#64748b] font-medium mt-1 text-sm md:text-base">Your registered account and KYC information.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* LEFT COLUMN: Main Profile Card */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white border border-[#f1f5f9] rounded-[30px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] text-center relative overflow-hidden flex flex-col items-center">
            
            {/* Cover Background */}
            <div className="absolute top-0 left-0 w-full h-28 bg-gradient-to-b from-[#e0f2fe] to-[#eff6ff] z-0"></div>
            
            {/* Profile Photo Container */}
            <div className="relative mt-14 mb-4 z-10">
              {photoUrl ? (
                <img 
                  src={photoUrl} 
                  alt="Profile" 
                  className="w-28 h-28 md:w-32 md:h-32 rounded-full object-cover border-[6px] border-white shadow-sm relative z-10 bg-white"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }} 
                />
              ) : (
                <div className="w-28 h-28 md:w-32 md:h-32 rounded-full bg-[#f8fafc] flex items-center justify-center text-4xl font-black text-[#3b82f6] border-[6px] border-white shadow-sm relative z-10">
                  {profileData?.first_name?.charAt(0)}{profileData?.last_name?.charAt(0)}
                </div>
              )}
              <div className="absolute bottom-2 right-2 bg-emerald-500 w-5 h-5 md:w-6 md:h-6 rounded-full border-[3px] md:border-4 border-white z-20" title="Active Account"></div>
            </div>

            {/* Profile Info */}
            <div className="relative z-10 px-6 pb-8 w-full flex flex-col items-center">
              <h2 className="text-2xl md:text-[26px] font-black text-[#0f172a] leading-tight mb-1">{profileData?.first_name} {profileData?.last_name}</h2>
              <p className="text-[#64748b] font-bold text-sm mb-6">ID: {profileData?.login_id || 'N/A'}</p>

              <div className="flex justify-between items-center bg-[#f8fafc] p-4 rounded-[20px] border border-[#f1f5f9] text-left w-full">
                <div>
                  <p className="text-[10px] text-[#94a3b8] font-bold uppercase tracking-widest mb-1">A/C Number</p>
                  <p className="text-[#0f172a] font-black text-sm md:text-base">{profileData?.account_number || 'N/A'}</p>
                </div>
                <div className="p-2.5 bg-white rounded-[12px] shadow-sm shrink-0"><Briefcase className="text-[#3b82f6]" size={20} strokeWidth={2.5}/></div>
              </div>
              
              <p className="text-[11px] font-bold text-[#94a3b8] mt-6 uppercase tracking-wider">Member since {joinDate}</p>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Details & Transactions */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Details Card */}
          <div className="bg-white border border-[#f1f5f9] rounded-[30px] p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div className="flex items-center gap-3 mb-6 border-b border-[#f1f5f9] pb-4">
              <div className="p-2 bg-emerald-50 rounded-[12px]"><ShieldCheck className="text-emerald-600" size={20} strokeWidth={2.5}/></div>
              <h3 className="text-lg md:text-xl font-black text-[#0f172a]">Verified Identity Details</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-6">
              <div>
                <p className="text-[10px] font-bold text-[#94a3b8] uppercase tracking-widest mb-2 flex items-center gap-1.5"><Mail size={14} strokeWidth={2.5}/> Email Address</p>
                <div className="bg-[#f8fafc] border border-[#f1f5f9] px-4 py-3.5 rounded-[16px] text-[#0f172a] font-bold break-all">{profileData?.email || 'N/A'}</div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-[#94a3b8] uppercase tracking-widest mb-2 flex items-center gap-1.5"><Phone size={14} strokeWidth={2.5}/> Mobile Number</p>
                <div className="bg-[#f8fafc] border border-[#f1f5f9] px-4 py-3.5 rounded-[16px] text-[#0f172a] font-bold">{profileData?.mobile_number || 'N/A'}</div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-[#94a3b8] uppercase tracking-widest mb-2 flex items-center gap-1.5"><Calendar size={14} strokeWidth={2.5}/> Date of Birth</p>
                <div className="bg-[#f8fafc] border border-[#f1f5f9] px-4 py-3.5 rounded-[16px] text-[#0f172a] font-bold">
                  {profileData?.dob ? new Date(profileData.dob).toLocaleDateString('en-GB') : 'N/A'}
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-[#94a3b8] uppercase tracking-widest mb-2 flex items-center gap-1.5"><CreditCard size={14} strokeWidth={2.5}/> PAN Number</p>
                <div className="bg-[#f8fafc] border border-[#f1f5f9] px-4 py-3.5 rounded-[16px] text-[#0f172a] font-black uppercase">{profileData?.pan_no || 'N/A'}</div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-[#94a3b8] uppercase tracking-widest mb-2 flex items-center gap-1.5"><User size={14} strokeWidth={2.5}/> Aadhar Number</p>
                <div className="bg-[#f8fafc] border border-[#f1f5f9] px-4 py-3.5 rounded-[16px] text-[#0f172a] font-black">
                  {profileData?.aadhar_no ? profileData.aadhar_no.replace(/(\d{4})/g, '$1 ').trim() : 'N/A'}
                </div>
              </div>
              <div className="md:col-span-2">
                <p className="text-[10px] font-bold text-[#94a3b8] uppercase tracking-widest mb-2 flex items-center gap-1.5"><MapPin size={14} strokeWidth={2.5}/> Full Address</p>
                <div className="bg-[#f8fafc] border border-[#f1f5f9] px-4 py-3.5 rounded-[16px] text-[#0f172a] font-bold leading-relaxed">{profileData?.full_address || profileData?.city || 'N/A'}</div>
              </div>
            </div>
          </div>

          {/* Recent Transactions Section */}
          <div className="bg-white border border-[#f1f5f9] rounded-[30px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div className="flex justify-between items-center mb-6 border-b border-[#f1f5f9] pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#eff6ff] rounded-[12px]"><ArrowRightLeft className="text-[#3b82f6]" size={20} strokeWidth={2.5} /></div>
                <h3 className="text-lg md:text-xl font-black text-[#0f172a]">Recent Transactions</h3>
              </div>
              <button 
                onClick={() => navigate('/account/transactions')}
                className="text-sm font-black text-[#3b82f6] hover:text-[#2563eb] transition-colors"
              >
                View All
              </button>
            </div>

            <div className="space-y-3">
              {recentTxns.length > 0 ? (
                recentTxns.map((tx, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 bg-[#f8fafc] border border-[#f1f5f9] rounded-[16px] hover:bg-white hover:shadow-sm hover:border-[#bfdbfe] transition-all">
                    <div className="flex items-center gap-3 md:gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center border shrink-0 ${tx.type === 'DEPOSIT' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : tx.type === 'WITHDRAW' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-[#eff6ff] text-[#3b82f6] border-[#bfdbfe]'}`}>
                        {tx.type === 'DEPOSIT' ? <ArrowDownRight size={20} strokeWidth={2.5} /> : <ArrowUpRight size={20} strokeWidth={2.5} />}
                      </div>
                      <div>
                        <p className="font-black text-[#0f172a] text-sm">{tx.type === 'BUY' ? `Bought ${tx.asset}` : tx.type}</p>
                        <p className="text-[11px] font-bold text-[#94a3b8]">{new Date(tx.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-black text-sm md:text-base ${tx.type === 'DEPOSIT' ? 'text-emerald-600' : 'text-[#0f172a]'}`}>
                        {tx.type === 'DEPOSIT' ? '+' : '-'}₹{parseFloat(tx.amount).toFixed(2)}
                      </p>
                      <p className={`text-[10px] font-black uppercase tracking-wider mt-0.5 ${tx.status === 'SUCCESS' ? 'text-emerald-500' : 'text-orange-500'}`}>{tx.status}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 text-[#94a3b8] font-bold text-sm">
                  <p>No recent transactions found.</p>
                </div>
              )}
            </div>
            
            <button 
              onClick={() => navigate('/account/transactions')}
              className="w-full mt-4 py-3.5 bg-[#f8fafc] hover:bg-[#0f172a] text-[#0f172a] hover:text-white font-black rounded-[16px] border border-[#f1f5f9] transition-all duration-300"
            >
              View All Transactions
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Profile;